import { Injectable, Optional } from '@angular/core';
import { Configuration } from '../../models/workflow/configuration';
import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { ConfigurationService } from '../../../entry-routes/configuration/configuration.service';
import { Url } from '../../models/url';
import { Observable } from 'rxjs';
import { AttachmentResponse } from '../../models/workflow/attachmentResponse';
import { CustomHttpUrlEncodingCodec } from '../../models/workflow/customHttpUrlEncodingCodecEncoder';
import { TaskAttachmentCollectionResource } from '../../models/workflow/taskAttachmentCollectionResource';

@Injectable({
  providedIn: 'root'
})
export class WkfTaskAttachmentService {
  protected basePath;
  public configuration = new Configuration();

  constructor(
    private httpClient: HttpClient,
    private configAction: ConfigurationService,
    @Optional() configuration: Configuration
  ) {
    if ( configuration ) {
      this.configuration = configuration;
    }
  }

  private getBasePath(): string {
    if ( !this.basePath ) {
      const flowableProtectedApiBaseUrl = Url.getFlowableProtectedApiBaseUrl( this.configAction );
      console.debug( 'WkfTaskAttachmentService, flowableProtectedApiBaseUrl : ' + flowableProtectedApiBaseUrl );
      this.basePath = flowableProtectedApiBaseUrl + Url.WKF_PROCESS_API;
    }
    console.debug( 'WkfTaskAttachmentService, this.basePath : ' + this.basePath );
    return this.basePath;
  }


  /**
   * @param consumes string[] mime-types
   * @return true: consumes contains 'multipart/form-data', false: otherwise
   */
  private canConsumeForm(consumes: string[]): boolean {
    const form = 'multipart/form-data';
    for (const consume of consumes) {
      if (form === consume) {
        return true;
      }
    }
    return false;
  }

  /**
   * Create a new attachment on a task, containing a link to an external resource or an attached file
   * This endpoint can be used in 2 ways: By passing a JSON Body (AttachmentRequest) to link an external resource or by passing a multipart/form-data Object to attach a file. NB: Swagger V2 specification does not support this use case that is why this endpoint might be buggy/incomplete if used with other tools.
   * @param taskId
   * @param body create an attachment containing a link to an external resource
   * @param file Attachment file
   * @param name Required name of the variable
   * @param description Description of the attachment, optional
   * @param type Type of attachment, optional. Supports any arbitrary string or a valid HTTP content-type.
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */
  public createAttachment(taskId: string, body?: TaskAttachmentCollectionResource, file?: Blob, name?: string, description?: string, type?: string, observe?: 'body', reportProgress?: boolean): Observable<AttachmentResponse>;
  public createAttachment(taskId: string, body?: TaskAttachmentCollectionResource, file?: Blob, name?: string, description?: string, type?: string, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<AttachmentResponse>>;
  public createAttachment(taskId: string, body?: TaskAttachmentCollectionResource, file?: Blob, name?: string, description?: string, type?: string, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<AttachmentResponse>>;
  public createAttachment(taskId: string, body?: TaskAttachmentCollectionResource, file?: Blob, name?: string, description?: string, type?: string, observe: any = 'body', reportProgress = false ): Observable<any> {

    if (taskId === null || taskId === undefined) {
      throw new Error('Required parameter taskId was null or undefined when calling createAttachment.');
    }

    let headers = new HttpHeaders();

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      'application/json'
    ];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected != undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = [
      'application/json',
      'multipart/form-data'
    ];
    /*const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected != undefined) {
        headers = headers.set('Content-Type', httpContentTypeSelected);
    }
    */


    const canConsumeForm = this.canConsumeForm(consumes);

    let formParams: { append(param: string, value: any): void | HttpParams; };
    let useForm = false;
    // const convertFormParamsToString = false;

    // use FormData to transmit files using content-type "multipart/form-data"
    // see https://stackoverflow.com/questions/4007969/application-x-www-form-urlencoded-or-multipart-form-data
    useForm = canConsumeForm;
    if (useForm) {
      formParams = new FormData();
    } else {
      formParams = new HttpParams({encoder: new CustomHttpUrlEncodingCodec()});
    }

    if (file !== undefined) {
      formParams = formParams.append('file', <any>file) || formParams;
    }
    if (name !== undefined) {
      formParams = formParams.append('name', <any>name) || formParams;
    }
    if (description !== undefined) {
      formParams = formParams.append('description', <any>description) || formParams;
    }
    if (type !== undefined) {
      formParams = formParams.append('type', <any>type) || formParams;
    }


    // headers = headers.set('Content-Type', 'multipart/form-data');


    return this.httpClient.post<AttachmentResponse>(`${this.basePath}/runtime/tasks/${encodeURIComponent(String(taskId))}/attachments`,
      formParams,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        /*{
            'Accept': 'application/json',
            'X-Skip-Error-Interceptor': '',
            'X-Skip-Header-Interceptor': ''
        },*/
        observe: observe,
        reportProgress: reportProgress
      }
    );
  }
}
